import React from 'react';
import { graphql, readInlineData } from 'react-relay';
import get from 'lodash.get';
import storage from 'dibs-browser-storage';
import { FormattedMessage, FormattedNumber } from 'react-intl';

const LAST_BANNER_MESSAGE_ID = 'lastBannerMessageId';
const MESSAGE_IMPRESSION_CAPS = 'messageImpressionCaps';
const BANNER_CLOSED_MANUALLY = 'bannerClosedManually';
const TERMS_URL = '/promotions/terms/?promo=';
const PROMO_MESSAGE_PLACEHOLDER = '{discount}';

const GLOBAL_FALLBACK_TEXT = (
    <FormattedMessage
        id="dbl.Header.MessageBanner.global_fallback_text"
        defaultMessage="1stdibs is the world's leading marketplace for beautiful and rare objects"
    />
);

const shownIds = {};

const messagesFragment = graphql`
    fragment messageHelpers_messages on MessageSetModule @inline {
        items {
            note {
                content {
                    message
                }
                anchors {
                    content {
                        message
                    }
                }
            }
        }
    }
`;

const viewerFragment = graphql`
    fragment messageHelpers_viewer on Viewer @inline {
        promoCodeMessaging: featureFlag(feature: "promoCodeMessaging")
    }
`;

const userFragment = graphql`
    fragment messageHelpers_user on User @inline {
        profile {
            currency
        }
        activePromotion {
            buyerDisplayable
            flatAdjustmentRate {
                rate {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
            code
            promotionMeta {
                customBannerMessage
                desktopBannerMessage
                desktopBannerCta
                mobileBannerMessage
                mobileBannerCta
            }
        }
    }
`;

export const getMessageTitle = messageItem => messageItem?.note?.content?.title || '';

export const getTrackingObj = ({ messageItem, action, label }) => {
    return {
        category: 'promo interaction',
        action,
        label: label || getMessageTitle(messageItem),
    };
};

const messageItemTemplate = ({ desktop = null, mobile = null }) => {
    return {
        note: {
            serviceId: 'template_id_1',
            content: desktop,
            anchors: [
                {
                    serviceId: 'template_id_2',
                    content: mobile,
                },
            ],
        },
    };
};

const globalFallbackMessage = () => {
    const content = {
        title: 'global fallback',
        message: GLOBAL_FALLBACK_TEXT,
    };
    return messageItemTemplate({
        desktop: content,
        mobile: content,
    });
};

const isLowResolution = typeof window !== 'undefined' && window.innerWidth < 1024;

export const getNoteByDevice = messageItem => {
    if (isLowResolution) {
        return get(messageItem, 'note.anchors[0]');
    }
    return get(messageItem, 'note');
};

const getNoteId = messageItem => get(getNoteByDevice(messageItem), 'serviceId');

const hasAnyText = messageItem => !!get(getNoteByDevice(messageItem), 'content.message');

const getMessageItemsWithText = messageItems =>
    (messageItems || []).filter(messageItem => hasAnyText(messageItem));

export const hasAnyMessagesByDevice = (messageSetRef = [], userRef) => {
    let hasPromoMessageByDevice = false;
    if (userRef) {
        const { activePromotion } = readInlineData(userFragment, userRef) || {};
        const { promotionMeta } = activePromotion || {};
        const { desktopBannerMessage, mobileBannerMessage } = promotionMeta || {};
        hasPromoMessageByDevice = isLowResolution ? !!mobileBannerMessage : !!desktopBannerMessage;
    }
    return (
        hasPromoMessageByDevice ||
        messageSetRef.some(messageSetRefItem => {
            const { items } = readInlineData(messagesFragment, messageSetRefItem);
            return items?.some(messageItem => hasAnyText(messageItem));
        })
    );
};

const messagePreviewMatch = () => window.location.search.match(/cmsMessagePreview=([^&]+)/);

const messageSetsMatch = () => window.location.search.match(/message-sets:([^&]+)/);

export const isPreview = () => !!(messagePreviewMatch() || messageSetsMatch());

const getMessagesFromUrl = () => {
    const match = messagePreviewMatch();
    if (match) {
        return [messageItemTemplate(JSON.parse(decodeURIComponent(match[1])))];
    }
    return null;
};

const getUserTypeAndSetIdFromUrl = () => {
    const setMatch = messageSetsMatch();
    if (setMatch) {
        const splits = setMatch[1].split('=');
        return {
            userType: splits[0],
            setId: splits.length > 1 ? splits[1] : null,
        };
    }
    return null;
};

export const getPreview = () => {
    if (isPreview()) {
        return {
            messages: getMessagesFromUrl(),
            setParams: getUserTypeAndSetIdFromUrl(),
        };
    }

    return {};
};

export const getPreviewVariables = () => {
    return getUserTypeAndSetIdFromUrl() || {};
};

export const addToShownList = messageItem => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        shownIds[noteId] = true;
    }
};

export const wasShown = messageItem => !!shownIds[getNoteId(messageItem)];

const increaseImpressionCap = noteId => {
    const { localStorage } = storage;
    const msgImpressCaps = localStorage.getItem(MESSAGE_IMPRESSION_CAPS) || {};

    msgImpressCaps[noteId] = msgImpressCaps[noteId] ? msgImpressCaps[noteId] + 1 : 1;

    localStorage.setItem(MESSAGE_IMPRESSION_CAPS, msgImpressCaps);
};

const setNoteIdToStorage = noteId => {
    const { localStorage } = storage;
    localStorage.setItem(LAST_BANNER_MESSAGE_ID, noteId);
};

export const setValuesToStorage = messageItem => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        increaseImpressionCap(noteId);
        setNoteIdToStorage(noteId);
    }
};

const validImpressionCap = messageItem => {
    const noteId = getNoteId(messageItem);
    const { localStorage } = storage;
    const msgImpressCaps = localStorage.getItem(MESSAGE_IMPRESSION_CAPS);
    if (msgImpressCaps && msgImpressCaps[noteId]) {
        return messageItem.impressionCap > msgImpressCaps[noteId];
    }
    return true;
};

const getNextMessageIndex = (allMessages, validMessages) => {
    const { localStorage } = storage;
    const lastMsgId = localStorage.getItem(LAST_BANNER_MESSAGE_ID);
    if (lastMsgId) {
        const lastMsgIndex = allMessages.findIndex(
            item => getNoteId(item) === lastMsgId.toString()
        );
        for (let i = 0; i < allMessages.length; i++) {
            const nextMsgItem = allMessages[(i + (lastMsgIndex + 1)) % allMessages.length];
            const indexInValidArray = validMessages.findIndex(
                item => getNoteId(item) === getNoteId(nextMsgItem)
            );
            if (indexInValidArray !== -1) {
                return indexInValidArray;
            }
        }
    }
    return 0;
};

const countAllMessages = messages => {
    return messages.filter(item => hasAnyText(item)).length || 0;
};

const getValidMessagesArray = messages => {
    return messages.filter(item => hasAnyText(item) && validImpressionCap(item));
};

const getFallbackMessagesArray = messageSet => {
    let fallback = get(messageSet, '[2].items', []).filter(item => hasAnyText(item));
    if (!fallback || !fallback.length) {
        fallback = [globalFallbackMessage()];
    }
    return fallback;
};

const moveItemToFront = (messages, index) => {
    return messages.splice(index).concat(messages);
};

export const getIsFirstTimeVisitor = () => {
    const { localStorage } = storage;
    const { totalVisitCount } = localStorage.getItem('visitCount') || {};
    const { session } = localStorage.getItem('pseudoSessionVisitCount') || {};

    return totalVisitCount <= session;
};

export const getMessagesToShow = messageSet => {
    const allMessages = messageSet[0].items.concat(messageSet[1].items);
    const allMessagesCount = countAllMessages(allMessages);
    if (allMessagesCount) {
        const validMessages = getValidMessagesArray(allMessages);
        if (validMessages && validMessages.length) {
            return moveItemToFront(validMessages, getNextMessageIndex(allMessages, validMessages));
        }
    }
    return getFallbackMessagesArray(messageSet);
};

export const getAllPreviewMessagesToShow = messageSet => {
    const primaryMessages = messageSet?.[0] || {};
    const secondaryMessages = messageSet?.[1] || {};

    // Single preview message from url.
    if (primaryMessages.note) {
        return messageSet;
    }

    // Preview message set from url.
    return (
        getMessageItemsWithText(primaryMessages.items)
            .concat(getMessageItemsWithText(secondaryMessages.items))
            .concat(getFallbackMessagesArray(messageSet)) || []
    );
};

export const isMessageBannerClosedManually = () => {
    const { sessionStorage } = storage;

    return sessionStorage.getItem(BANNER_CLOSED_MANUALLY);
};

export const setIsBannerClosedManuallyInStorage = () => {
    const { sessionStorage } = storage;
    sessionStorage.setItem(BANNER_CLOSED_MANUALLY, true);
};

export const getPromoMessage = (viewer, user) => {
    const { promoCodeMessaging } = readInlineData(viewerFragment, viewer);
    const { activePromotion, profile } = readInlineData(userFragment, user) || {};
    const { promotionMeta, code, flatAdjustmentRate, buyerDisplayable } = activePromotion || {};
    const flatRate = flatAdjustmentRate?.rate || {};
    const {
        customBannerMessage,
        desktopBannerMessage,
        desktopBannerCta,
        mobileBannerMessage,
        mobileBannerCta,
    } = promotionMeta || {};

    if (promoCodeMessaging && buyerDisplayable && (desktopBannerMessage || mobileBannerMessage)) {
        const updateBannerMessage = message => {
            if (message && !customBannerMessage && flatAdjustmentRate) {
                const amount = (flatRate.convertedAmountList || []).find(
                    c => c.currency === profile.currency
                )?.amount;
                const splitMessage = message.split(PROMO_MESSAGE_PLACEHOLDER);
                return (
                    <>
                        {splitMessage[0]}
                        <FormattedNumber
                            value={amount}
                            style="currency"
                            currency={profile.currency}
                            minimumFractionDigits={0}
                            maximumFractionDigits={0}
                        />
                        {splitMessage[1]}
                    </>
                );
            }
            return message;
        };
        const url = TERMS_URL + code;
        const message = {
            note: {
                content: {
                    message: updateBannerMessage(desktopBannerMessage),
                    cta: {
                        text: desktopBannerCta,
                        url,
                    },
                },
                anchors: [
                    {
                        content: {
                            message: updateBannerMessage(mobileBannerMessage),
                            cta: {
                                text: mobileBannerCta,
                                url,
                            },
                        },
                    },
                ],
            },
        };
        return hasAnyText(message) ? [message] : null;
    }
    return null;
};
